import {
  Component,
  input,
  output,
  signal,
  inject,
  computed,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { DropdownModule } from 'primeng/dropdown';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { mensajeCamposInvalidos } from 'src/app/common/mensajes-validacion';
import {
  DEFAULT_ROWS_PER_PAGE,
  PAGE_SIZE_OPTIONS
} from 'src/app/common/constants-ejb';
import {
  sanitizeTextPreserveSpaces,
  sanitizeEmailContacto,
  emailKeypress,
  onlyLettersKeypress,
  onlyNumbersKeypress,
  isValidEmailContactoLegacy,
  normalizarTextoLegacy,
  CONTACT_MAX_LENGTHS
} from 'src/app/common/input-utils';
import { contactosTrabajoDialogPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';
import {
  AccionPaginacionContactos,
  PaginacionServidorContactos
} from 'src/app/presentation/shared/contactos/contactos-cursor-pager';

export type {
  AccionPaginacionContactos,
  PaginacionServidorContactos
} from 'src/app/presentation/shared/contactos/contactos-cursor-pager';

/**
 * Estructura de un contacto del centro de trabajo.
 * Migrado desde CrearCitaAdministrativaViewImpl - BuscarContacto
 */
export interface ContactoCentroTrabajo {
  nmconsecutivo?: number;
  cdpersona?: number;
  dsnombre: string;
  dscargo?: string;
  dstelefonoFijo?: string;
  dstelefonoCelular?: string;
  dsemail?: string;
}


/**
 * Diálogo de Contactos del Centro de Trabajo del Cliente.
 *
 * Flujo Legacy:
 * 1. Se abre desde la lupa del campo Contacto en CrearCitaAdministrativaViewImpl
 * 2. Muestra lista de contactos existentes del centro de trabajo
 * 3. Permite: Nuevo (crear contacto), Modificar, Seleccionar, Cerrar
 * 4. Al seleccionar, llena los campos: Contacto, Teléfono fijo, Celular, Email
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Component({
  selector: 'app-contactos-trabajo-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    TooltipModule,
    DropdownModule,
    ConfirmDialogModule,
    BusquedaPaginadorComponent
  ],
  providers: [ConfirmationService],
  styleUrls: ['../../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      [(visible)]="visible"
      [header]="
        modoEdicion()
          ? contactoEditando()
            ? 'Modificar Contacto'
            : 'Nuevo Contacto'
          : 'Contactos del Centro de Trabajo del Cliente'
      "
      [modal]="true"
      [style]="{ width: '96vw', maxWidth: modoEdicion() ? '32.5rem' : '61rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '94vw', '768px': '96vw', '640px': '98vw' }"
      [closable]="true"
      (onHide)="onClose.emit()"
    >
      <!-- Vista: Lista de contactos -->
      @if (!modoEdicion()) {
        <!-- Botón Nuevo arriba a la derecha -->
        <div
          class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-3"
        >
          <span class="text-sm text-gray-600">
            @if (totalContactos() > 0) {
              Registros del {{ registroInicio() }} hasta el
              {{ registroFin() }} de {{ totalContactos() }}
            }
          </span>
          <button
            pButton
            label="Nuevo"
            icon="pi pi-plus"
            class="p-button-success p-button-sm min-w-[44px] min-h-[44px] w-full sm:w-auto"
            (click)="nuevoContacto()"
          ></button>
        </div>

        <div class="md:hidden space-y-3 p-3">
          @if (paginatedContactos().length === 0) {
            <div class="text-center text-gray-500 py-4">
              No se recuperó información con los parámetros ingresados.
            </div>
          }
          @for (
            contacto of paginatedContactos();
            track trackContacto(contacto)
          ) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 min-w-0">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  Nombre
                </div>
                <div
                  class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                  [attr.title]="tituloTabla(contacto.dsnombre)"
                >
                  {{ textoTabla(contacto.dsnombre) }}
                </div>
              </div>
              <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Cargo:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(contacto.dscargo)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Teléfono fijo:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(contacto.dstelefonoFijo)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Celular:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(contacto.dstelefonoCelular)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Email:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(contacto.dsemail)
                  }}</span>
                </div>
              </div>
              <div class="grid grid-cols-1 gap-2">
                <button
                  pButton
                  label="Modificar"
                  class="p-button-sm p-button-outlined min-h-[44px]"
                  (click)="editarContacto(contacto)"
                ></button>
                @if (!ocultarSeleccionar()) {
                  <button
                    pButton
                    label="Seleccionar"
                    class="p-button-sm min-h-[44px]"
                    (click)="seleccionarContacto(contacto)"
                  ></button>
                }
                @if (ocultarSeleccionar()) {
                  <button
                    pButton
                    label="Eliminar"
                    class="p-button-sm p-button-danger p-button-outlined min-h-[44px]"
                    (click)="eliminarContacto(contacto)"
                  ></button>
                }
              </div>
            </div>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="contactosTableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="paginatedContactos()"
            [paginator]="false"
            [rows]="tamanoPagina()"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [scrollHeight]="
              contactosTableConfig().responsive.scrollHeight || '320px'
            "
            [styleClass]="contactosTableStyleClass()"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Nombre">Nombre</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Cargo">Cargo</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Teléfono fijo"
                    >Teléfono fijo</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Teléfono celular"
                    >Teléfono celular</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Email">Email</span>
                </th>
                <th pResizableColumn style="width: 80px">
                  <span class="grid-header-text" title="Modificar"
                    >Modificar</span
                  >
                </th>
                @if (!ocultarSeleccionar()) {
                  <th pResizableColumn style="width: 90px">
                    <span class="grid-header-text" title="Seleccionar"
                      >Seleccionar</span
                    >
                  </th>
                }
                @if (ocultarSeleccionar()) {
                  <th pResizableColumn style="width: 70px">
                    <span class="grid-header-text" title="Eliminar"
                      >Eliminar</span
                    >
                  </th>
                }
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-contacto>
              <tr>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(contacto.dsnombre)"
                    >{{ textoTabla(contacto.dsnombre) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(contacto.dscargo)"
                    >{{ textoTabla(contacto.dscargo) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(contacto.dstelefonoFijo)"
                    >{{ textoTabla(contacto.dstelefonoFijo) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(contacto.dstelefonoCelular)"
                    >{{ textoTabla(contacto.dstelefonoCelular) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(contacto.dsemail)"
                    >{{ textoTabla(contacto.dsemail) }}</span
                  >
                </td>
                <td class="text-center">
                  <a
                    class="text-blue-600 hover:underline cursor-pointer"
                    (click)="editarContacto(contacto)"
                    >Modificar</a
                  >
                </td>
                @if (!ocultarSeleccionar()) {
                  <td class="text-center">
                    <a
                      class="text-blue-600 hover:underline cursor-pointer"
                      (click)="seleccionarContacto(contacto)"
                      >Seleccionar</a
                    >
                  </td>
                }
                @if (ocultarSeleccionar()) {
                  <td class="text-center">
                    <a
                      class="text-red-600 hover:underline cursor-pointer"
                      (click)="eliminarContacto(contacto)"
                      >Eliminar</a
                    >
                  </td>
                }
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="7" class="text-center text-gray-500 py-4">
                  No se recuperó información con los parámetros ingresados.
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        @if (totalContactos() > 0) {
          <app-busqueda-paginador
            [currentPage]="paginaActual()"
            [totalPages]="totalPages()"
            [hasPrevious]="hasPrevious()"
            [hasNext]="hasNext()"
            [pageSizeOptions]="pageSizeOptions"
            [selectedPageSize]="tamanoPagina()"
            (pageSizeChange)="selectedPageSize = $event; onPageSizeChange()"
            (first)="irPrimeraPagina()"
            (previous)="irPaginaAnterior()"
            (next)="irPaginaSiguiente()"
            (last)="irUltimaPagina()"
          ></app-busqueda-paginador>
        }
      }

      <!-- Vista: Formulario de nuevo/editar contacto -->
      @if (modoEdicion()) {
        <div class="grid grid-cols-1 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1">
              Nombre <span class="text-red-500">*</span>
            </label>
            <input
              pInputText
              [(ngModel)]="contactoForm.dsnombre"
              [maxlength]="maxLengths.NOMBRE"
              class="w-full"
              (keypress)="onlyLettersKeypress($event)"
              (change)="filtrarSoloLetras($event); contactoForm.dsnombre = sanitizeTextPreserveSpaces(contactoForm.dsnombre)"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1"
              >Cargo</label
            >
            <input
              pInputText
              [(ngModel)]="contactoForm.dscargo"
              [maxlength]="maxLengths.CARGO"
              class="w-full"
              (change)="
                contactoForm.dscargo = sanitizeTextPreserveSpaces(
                  contactoForm.dscargo
                )
              "
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1">
              Teléfono fijo <span class="text-red-500">*</span>
            </label>
            <input
              pInputText
              [(ngModel)]="contactoForm.dstelefonoFijo"
              [maxlength]="maxLengths.TELEFONO"
              class="w-full"
              (keypress)="onlyNumbersKeypress($event)"
              (change)="filtrarSoloNumeros($event, 'dstelefonoFijo')"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1"
              >Teléfono celular</label
            >
            <input
              pInputText
              [(ngModel)]="contactoForm.dstelefonoCelular"
              [maxlength]="maxLengths.TELEFONO"
              class="w-full"
              (keypress)="onlyNumbersKeypress($event)"
              (change)="filtrarSoloNumeros($event, 'dstelefonoCelular')"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1">
              Email <span class="text-red-500">*</span>
            </label>
            <input
              pInputText
              [(ngModel)]="contactoForm.dsemail"
              [maxlength]="maxLengths.EMAIL"
              type="email"
              class="w-full"
              (keypress)="emailKeypress($event)"
              (change)="
                contactoForm.dsemail = sanitizeEmailContacto(contactoForm.dsemail)
              "
            />
          </div>
        </div>
      }

      <!-- Footer: Botones según el modo -->
      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          @if (!modoEdicion()) {
            <p-button
              label="Cerrar"
              icon="pi pi-times"
              [outlined]="true"
              styleClass="min-h-[44px]"
              (onClick)="visible = false; onClose.emit()"
            ></p-button>
          }
          @if (modoEdicion()) {
            <p-button
              label="Cancelar"
              icon="pi pi-times"
              [outlined]="true"
              styleClass="min-h-[44px]"
              (onClick)="cancelarEdicion()"
            ></p-button>
            <p-button
              label="Guardar"
              icon="pi pi-check"
              styleClass="min-h-[44px]"
              (onClick)="guardarContacto()"
            ></p-button>
          }
        </div>
      </ng-template>
    </p-dialog>

    <p-confirmDialog key="contactosTrabajoConfirm" />
  `
})
export class ContactosTrabajoDialogComponent {
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  // XSS Sanitization - exponer funciones al template
  sanitizeTextPreserveSpaces = sanitizeTextPreserveSpaces;
  sanitizeEmailContacto = sanitizeEmailContacto;
  emailKeypress = emailKeypress;
  onlyLettersKeypress = onlyLettersKeypress;
  onlyNumbersKeypress = onlyNumbersKeypress;
  readonly maxLengths = CONTACT_MAX_LENGTHS;

  // === Inputs ===
  contactos = input<ContactoCentroTrabajo[]>([]);
  cdpersona = input<number | null>(null);
  /** Si es true, oculta "Seleccionar" y muestra "Eliminar". Usado desde Programar Servicios. */
  ocultarSeleccionar = input<boolean>(false);
  /** Si se informa, la paginación es de servidor (ver PaginacionServidorContactos). */
  paginacionServidor = input<PaginacionServidorContactos | null>(null);
  /** El padre lo pone en true mientras carga/guarda: se refleja en la tabla (máscara de carga del legacy). */
  loading = input<boolean>(false);
  /**
   * Contador que el padre incrementa cuando falla el guardado de un contacto: el diálogo reabre el
   * formulario con lo digitado para no perderlo (el éxito lo informa el padre con su toast).
   */
  errorGuardado = input<number>(0);

  // === Outputs ===
  onClose = output<void>();
  /** Solicita al padre otra página (solo con paginacionServidor). */
  onPaginar = output<AccionPaginacionContactos>();
  onSelect = output<ContactoCentroTrabajo>();
  onSave = output<ContactoCentroTrabajo>();
  onUpdate = output<ContactoCentroTrabajo>();
  /** Emite cuando se solicita eliminar (inactivar) un contacto */
  onDelete = output<ContactoCentroTrabajo>();

  // === State ===
  visible = true;
  modoEdicion = signal(false);
  contactoEditando = signal<ContactoCentroTrabajo | null>(null);
  /** Último envío, para restaurar el formulario si el backend rechaza el guardado. */
  private ultimoEnvio: {
    form: ContactoCentroTrabajo;
    editando: ContactoCentroTrabajo | null;
  } | null = null;

  private readonly restaurarTrasError = effect(() => {
    const token = this.errorGuardado();
    if (token > 0 && this.ultimoEnvio) {
      this.contactoForm = { ...this.ultimoEnvio.form };
      this.contactoEditando.set(this.ultimoEnvio.editando);
      this.modoEdicion.set(true);
    }
  });

  // === Paginación (estándar) ===
  currentPage = signal(1);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  selectedPageSize = DEFAULT_ROWS_PER_PAGE;

  readonly contactosTableConfig = computed(() =>
    contactosTrabajoDialogPreset.createConfig({
      data: this.paginatedContactos() as unknown as Record<string, unknown>[],
      style: {
        tableClassName: 'tabla-contactos-trabajo',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      pagination: {
        enabled: false,
        mode: 'client',
        pageSize: this.pageSize(),
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        currentPageReportTemplate:
          'Registros del {first} hasta el {last} de {totalRecords}'
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No se recuperó información con los parámetros ingresados.'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '72rem',
        scrollHeight: '320px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de contactos del centro de trabajo',
        announceSelection: false,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly contactosTableStyleClass = computed(() => {
    const config = this.contactosTableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  // Paginación: de servidor cuando el padre informa `paginacionServidor`; si no, local (uso standalone)
  private readonly servidor = computed(() => this.paginacionServidor());

  paginaActual = computed(() => this.servidor()?.pagina ?? this.currentPage());
  tamanoPagina = computed(() => this.servidor()?.tamano ?? this.pageSize());

  totalContactos = computed(() => this.servidor()?.total ?? this.contactos().length);

  totalPages = computed(() => {
    const srv = this.servidor();
    if (srv) return srv.totalPaginas;
    const total = this.totalContactos();
    const size = this.pageSize();
    return total > 0 ? Math.ceil(total / size) : 0;
  });

  paginatedContactos = computed(() => {
    const todos = this.contactos();
    // Con paginación de servidor, `contactos` ya es la página actual
    if (this.servidor()) return todos;
    const page = this.currentPage();
    const size = this.pageSize();
    const start = (page - 1) * size;
    return todos.slice(start, start + size);
  });

  registroInicio = computed(() => {
    const srv = this.servidor();
    if (srv) return srv.inicio;
    if (this.totalContactos() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });

  registroFin = computed(() => {
    const srv = this.servidor();
    if (srv) return srv.fin;
    if (this.totalContactos() === 0) return 0;
    const fin = this.currentPage() * this.pageSize();
    return Math.min(fin, this.totalContactos());
  });

  hasNext = computed(() => {
    const srv = this.servidor();
    return srv ? srv.haySiguiente : this.currentPage() < this.totalPages();
  });
  hasPrevious = computed(() => {
    const srv = this.servidor();
    return srv ? srv.hayAnterior : this.currentPage() > 1;
  });

  // Formulario de contacto
  contactoForm: ContactoCentroTrabajo = {
    dsnombre: '',
    dscargo: '',
    dstelefonoFijo: '',
    dstelefonoCelular: '',
    dsemail: ''
  };

  /**
   * Abre el formulario para crear un nuevo contacto.
   */
  nuevoContacto(): void {
    this.contactoForm = {
      dsnombre: '',
      dscargo: '',
      dstelefonoFijo: '',
      dstelefonoCelular: '',
      dsemail: ''
    };
    this.contactoEditando.set(null);
    this.modoEdicion.set(true);
  }

  /**
   * Abre el formulario para editar un contacto existente.
   */
  editarContacto(contacto: ContactoCentroTrabajo): void {
    this.contactoForm = {
      ...contacto,
      nmconsecutivo: contacto.nmconsecutivo,
      cdpersona: contacto.cdpersona
    };
    this.contactoEditando.set(contacto);
    this.modoEdicion.set(true);
  }

  /**
   * Cancela la edición y vuelve a la lista.
   */
  cancelarEdicion(): void {
    this.modoEdicion.set(false);
    this.contactoEditando.set(null);
  }

  /**
   * Filtra el input para solo permitir letras, espacios y tildes.
   */
  filtrarSoloLetras(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Permitir letras (incluyendo tildes), espacios
    input.value = input.value.replace(/[^a-zA-ZÁÉÍÓÚáéíóúÑñ\s]/g, '');
    this.contactoForm.dsnombre = input.value;
  }

  /**
   * Filtra el input para solo permitir números.
   */
  filtrarSoloNumeros(
    event: Event,
    campo: 'dstelefonoFijo' | 'dstelefonoCelular'
  ): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
    this.contactoForm[campo] = input.value;
  }

  // === Métodos de Paginación (estándar) ===

  irPrimeraPagina(): void {
    if (this.servidor()) return this.onPaginar.emit({ tipo: 'first' });
    this.currentPage.set(1);
  }

  irPaginaAnterior(): void {
    if (this.servidor()) return this.onPaginar.emit({ tipo: 'previous' });
    if (this.hasPrevious()) {
      this.currentPage.update((p) => p - 1);
    }
  }

  irPaginaSiguiente(): void {
    if (this.servidor()) return this.onPaginar.emit({ tipo: 'next' });
    if (this.hasNext()) {
      this.currentPage.update((p) => p + 1);
    }
  }

  irUltimaPagina(): void {
    if (this.servidor()) return this.onPaginar.emit({ tipo: 'last' });
    this.currentPage.set(this.totalPages());
  }

  onPageSizeChange(): void {
    if (this.servidor()) {
      return this.onPaginar.emit({ tipo: 'size', size: this.selectedPageSize });
    }
    this.pageSize.set(this.selectedPageSize);
    this.currentPage.set(1); // Volver a la primera página
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  trackContacto(contacto: ContactoCentroTrabajo): number | string {
    return contacto.nmconsecutivo ?? contacto.cdpersona ?? contacto.dsnombre;
  }

  /**
   * Valida formato de email con Constants.REGEX_EMAILS del legacy (widgetEmail.setRegex).
   */
  private validarEmail(email: string): boolean {
    return isValidEmailContactoLegacy(email);
  }

  /**
   * Valida y guarda el contacto.
   * Validaciones (migrado del legacy AdminContactosViewImpl.validateRequiredFields):
   * - Nombre: obligatorio, solo letras
   * - Teléfono fijo: obligatorio
   * - Email: obligatorio con formato válido
   */
  guardarContacto(): void {
    const errores: string[] = [];

    // Validar nombre obligatorio
    if (!this.contactoForm.dsnombre?.trim()) {
      errores.push('Nombre');
    }

    // Validar teléfono fijo obligatorio
    if (!this.contactoForm.dstelefonoFijo?.trim()) {
      errores.push('Teléfono fijo');
    }

    // Validar email obligatorio
    if (!this.contactoForm.dsemail?.trim()) {
      errores.push('Email');
    }

    if (errores.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Campos obligatorios',
        detail: mensajeCamposInvalidos(errores)
      });
      return;
    }

    // Validar formato de email
    if (!this.validarEmail(this.contactoForm.dsemail!)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: mensajeCamposInvalidos(['Email'])
      });
      return;
    }

    // Legacy (ListaContactosViewImpl): Nombre y Cargo pasan por Utilidades.cleanString y
    // toUpperCase (getTextBoxChangeHandler()) — sin tildes, solo letras y espacios, en mayúsculas
    this.contactoForm.dsnombre = normalizarTextoLegacy(this.contactoForm.dsnombre).trim();
    this.contactoForm.dscargo = normalizarTextoLegacy(this.contactoForm.dscargo).trim();

    this.ultimoEnvio = {
      form: { ...this.contactoForm },
      editando: this.contactoEditando()
    };

    // Diferenciar entre crear y modificar basado en si tiene nmconsecutivo
    if (this.contactoEditando()) {
      // MODIFICAR: tiene nmconsecutivo existente
      this.onUpdate.emit({ ...this.contactoForm });
    } else {
      // CREAR: contacto nuevo
      this.onSave.emit({ ...this.contactoForm });
    }

    // Volver a la lista de contactos (NO cerrar el diálogo)
    // El mensaje de éxito lo muestra el padre cuando el backend responde OK
    this.modoEdicion.set(false);
    this.contactoEditando.set(null);
  }

  /**
   * Selecciona un contacto y lo envía al formulario principal.
   */
  seleccionarContacto(contacto: ContactoCentroTrabajo): void {
    this.onSelect.emit(contacto);
    this.visible = false;
  }

  /**
   * Solicita eliminar (inactivar) un contacto.
   * Legacy (SuraTable.addDeleteColumn): siempre pide confirmación antes de eliminar.
   * Emite al padre, tras confirmar, para que realice la inactivación en BD.
   */
  eliminarContacto(contacto: ContactoCentroTrabajo): void {
    this.confirmationService.confirm({
      key: 'contactosTrabajoConfirm',
      header: 'Confirmación',
      message: '¿Esta seguro que desea eliminar el registro seleccionado?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí',
      rejectLabel: 'No',
      accept: () => this.onDelete.emit(contacto)
    });
  }
}
